// Préfixe de langue dans l'URL — module pur (Phase 96, D-03).
//
// Ni React, ni window, ni routeur : l'appelant lit `window.location.pathname`
// ou `useLocation().pathname` et passe la chaîne en argument, exactement comme
// detect.ts reçoit ses balises BCP-47. C'est ce qui rend le cas `/enveloppe`
// prouvable sans monter jsdom.
//
// Ce module est la source unique du préfixe : le provider (D-03), la bascule
// (D-05), le bandeau (D-07) et le sitemap le lisent tous d'ici. Un second
// littéral `'/en'` recopié ailleurs serait une dérive en attente.
// Refs: phase 96.
import type { Lang } from './types'

/** Le seul segment de langue de l'application. Le français n'est PAS préfixé. */
export const EN_PREFIX = '/en'

/**
 * Langue portée par un chemin, ou `null` quand il n'en porte aucune (français
 * implicite). Rend `null` — pas le repli — comme `normalizeLang` : c'est
 * l'appelant qui décide quoi faire d'une absence de préfixe.
 *
 * WR-03 (convention de lib/loginCooldown.ts) : ce module POSSÈDE la validation
 * de son entrée, qui est intégralement contrôlée par le visiteur. La
 * comparaison est donc EXACTE — `=== EN_PREFIX` ou `startsWith(EN_PREFIX + '/')`.
 * La forme relâchée — un `startsWith` du préfixe SANS sa barre finale —
 * rendrait `'en'` sur `/enveloppe` ;
 * React Router en retirerait alors `/en` par `basename`, chercherait
 * `/veloppe`, ne matcherait aucune route et rendrait l'application
 * ENTIÈREMENT blanche — pas une page, le site.
 */
export function langFromPath(pathname: string): 'en' | null {
  if (pathname === EN_PREFIX) return 'en'
  return pathname.startsWith(`${EN_PREFIX}/`) ? 'en' : null
}

/**
 * Chemin jumeau d'une page dans l'autre langue. Idempotente (reposer la langue
 * déjà portée ne change rien) et involutive par paire :
 * `withLang(withLang(p, 'en'), 'fr') === p` pour tout chemin nu.
 *
 * `/` devient `/en/` et non `/en` : une forme unique de la racine anglaise,
 * sinon deux URLs canoniques désigneraient la même page.
 */
export function withLang(pathname: string, lang: Lang): string {
  const bare = langFromPath(pathname) ? pathname.slice(EN_PREFIX.length) || '/' : pathname
  return lang === 'en' ? `${EN_PREFIX}${bare}` : bare
}
